import React, { ReactNode } from "react";

interface StyledInteractiveButtonProps {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  isActive?: boolean;
  variant?: 'tab' | 'expandable';
  expandIcon?: ReactNode;
  className?: string;
}

const StyledInteractiveButton = ({ 
  children, 
  onClick, 
  disabled = false, 
  isActive = false,
  variant = 'tab',
  expandIcon,
  className = ""
}: StyledInteractiveButtonProps) => {
  const baseStyle = {
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: "500",
    textAlign: "center" as const,
    borderRadius: "6px",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    backgroundColor: isActive 
      ? "rgba(255, 193, 7, 0.1)" 
      : "rgba(255, 255, 255, 0.02)",
    color: isActive ? "#ffc107" : "rgba(255, 255, 255, 0.7)",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
    opacity: disabled ? 0.5 : 1,
    display: "flex",
    alignItems: "center",
    gap: "6px",
    boxShadow: isActive 
      ? "0 0 0 1px rgba(255, 193, 7, 0.3)" 
      : "none",
    width: "100%"
  };

  const expandableStyle = {
    ...baseStyle,
    justifyContent: "space-between" as const,
    textAlign: "center" as const,
    color: isActive ? "#ffc107" : "white"
  };

  const tabStyle = {
    ...baseStyle,
    flex: 1,
    justifyContent: "center" as const
  };

  const currentStyle = variant === 'expandable' ? expandableStyle : tabStyle;

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled && !isActive) {
      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
      e.currentTarget.style.color = variant === 'expandable' ? "white" : "rgba(255, 255, 255, 0.9)";
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled && !isActive) {
      e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.02)";
      e.currentTarget.style.color = variant === 'expandable' ? "white" : "rgba(255, 255, 255, 0.7)";
    }
  };

  return (
    <div
      className={className}
      style={currentStyle}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
            {variant === 'expandable' ? (
        <>
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
            {children}
          </div>
          {expandIcon && <span>{expandIcon}</span>}
        </>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          {children}
        </div>
      )}
    </div>
  );
};

export default StyledInteractiveButton; 